import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import clsx from "clsx";

import * as NavigationUtils from "../../../lib/util/NavigationUtils";
import AutoFocus from "../../common/AutoFocus/AutoFocus";
import SearchFilter from "../../common/SearchFilter/SearchFilter";
import { sendEvent, TrackingContext } from "../../../lib/util/AnalyticsUtils";
import { MISC_FIND_GROUP } from "../../../strings";
import { LocaleContext, translate } from "../../../lib/util/LocaleUtils";

import MenuItem from "./sub-components/MenuItem";
import { MenuContext } from "../Menu/MenuContext";

interface MenuItemsProps {
	onProvideKeyboardCallback?: (handleKeyDown: (e: any) => void) => void;
	routes: any[];
	filteredRoutes?: any[];
	className?: string;
	onFilterItem?: (item: any) => boolean;
	noInk?: boolean;
	superscriptIcons?: any;
	autoFocus?: boolean;
	allowSearch?: boolean;
	onFilter?: (items: any[]) => void;
	onSelect: (item: any) => void;
	selected?: string | null;
}

// TODO: Cleanup this.
/* TODO: Selection cannot be keyboard controlled for controlled component.
         Move selection to selection + hightlight (focus)? */
const MenuItems: React.FC<MenuItemsProps> = (props) => {
	const localeContext = useContext(LocaleContext);
	const trackingContext = useContext(TrackingContext);
	const menuContext = useContext(MenuContext);
	const [active, setActive] = useState(0);
	const ref = useRef(null);

	useEffect(() => {
		const { onProvideKeyboardCallback } = props;
		if (onProvideKeyboardCallback) {
			onProvideKeyboardCallback(handleKeyDown);
		}
	});

	const getElement = (i: number) => {
		return (ref.current as any).querySelectorAll(".MenuItem")[i];
	};

	const {
		routes,
		filteredRoutes,
		className,
		onFilterItem,
		noInk,
		superscriptIcons,
		autoFocus,
		allowSearch,
		onFilter,
		onSelect,
	} = props;

	const menuOpen = menuContext?.visible;

	const routeCount = routes.length;

	const handleFilter = useCallback(
		(filteredLabels) => {
			const filteredItems = routes.filter((route) =>
				filteredLabels.includes(route.displayName || route.label),
			);
			if (onFilter) {
				onFilter(filteredItems);
			}
		},
		[onFilter, routes],
	);

	const setItemActive = useCallback(
		(active: number) => {
			let clampedActive = active;
			if (clampedActive < 0) clampedActive = routeCount - 1;
			if (clampedActive >= routeCount) clampedActive = 0;
			setActive(clampedActive);
		},
		[routeCount],
	);

	const handleKeyDown = useCallback(
		(e: any) => {
			if (e.key === "ArrowDown" || e.key === "ArrowRight" || e.key === "Tab") {
				e.preventDefault();
				e.stopPropagation();
				setItemActive(active + 1);
			} else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
				e.preventDefault();
				e.stopPropagation();
				setItemActive(active - 1);
			} else if (e.key === "Enter") {
				e.preventDefault();
				e.stopPropagation();
				const element = getElement(active);
				if (!element.classList.contains("--disabled")) {
					element.click();
				}
			}
		},
		[active, setItemActive],
	);

	const handleClick = useCallback(
		(e: any, item: any) => {
			menuContext?.setVisible(false);
			setItemActive(item.index);

			if (onSelect) {
				onSelect(item);
			}

			if (!item.noRedirect && item.pathName) {
				NavigationUtils.link(item.pathName, e);
			} else {
				e.stopPropagation();
				e.preventDefault();
			}

			sendEvent({
				eventCategory: `MenuItem - ${item.id}`,
				eventAction: "click",
				eventLabel: `${trackingContext}`,
			});
		},
		[menuContext, onSelect, setItemActive, trackingContext],
	);

	const handleActive = useCallback(
		(item: any) => {
			setItemActive(item.index);
		},
		[setItemActive],
	);

	const currentSelection = props.selected;

	const content = useMemo(
		() =>
			(filteredRoutes || routes).reduce((array, item, index) => {
				if (item.id === undefined) {
					throw new Error(
						`All nav items must have an ID, link '${item.displayName}' does not`,
					);
				}

				// Abort early if it doesn't match the filter condition
				if (onFilterItem) {
					if (!onFilterItem(item)) return array;
				}

				const selected = item.id === currentSelection;
				const isActive = index === active;

				array.push(
					<MenuItem
						key={item.id || index}
						index={index}
						onSelect={handleClick}
						active={isActive}
						onActive={handleActive}
						selected={selected}
						noInk={noInk}
						item={item}
						className={item.className}
						superscriptIcons={superscriptIcons}
					/>,
				);

				return array;
			}, []),
		[
			active,
			currentSelection,
			filteredRoutes,
			handleActive,
			handleClick,
			noInk,
			onFilterItem,
			routes,
			superscriptIcons,
		],
	);

	return (
		<div
			role={"menu"}
			tabIndex={0}
			ref={ref}
			className={clsx("MenuItems", className)}
			onKeyDown={handleKeyDown}
		>
			{allowSearch && (
				<SearchFilter
					open={menuOpen}
					items={routes}
					searchProps={["displayName", "label"]}
					placeholder={translate(MISC_FIND_GROUP, localeContext.locale)}
					onFilter={handleFilter}
					trackingContext={trackingContext}
				/>
			)}
			{autoFocus ? <AutoFocus>{content}</AutoFocus> : content}
		</div>
	);
};

export default MenuItems;
